<template>
  <div class="flex flex-col items-center">
    <AlertBanner v-if="hasPhotoRejected" id="submit-error-alert" :status="0" class="mt-0 sm:mt-32 lg:mt-0" style="width: 100%">
      <div>
        Your profile photo did not meet our
        <a
          target="_blank"
          href="https://support.sittercity.com/hc/en-us/articles/115012435268-Profile-photo-guidelines-for-sitters"
          class="primary-link"
          >Photo Guidelines</a
        >. Check the guidelines and <a href="/sitter/photo_upload#/sitter_upload" class="primary-link">upload a new photo</a>.
      </div>
    </AlertBanner>
    <AlertBanner v-if="isPhotoUploadAlert" id="photo-upload-alert" :status="1" class="mt-0 sm:mt-32 lg:mt-0" style="width: 100%">
      <div>Your photo is saved and will be reviewed by our team shortly.</div>
    </AlertBanner>
    <AlertBanner v-if="hasExternalReviewSitterError" id="external-reviews-sitter-error" :status="3" style="width: 100%">
      <div>Only parents can leave a review</div>
    </AlertBanner>
    <div class="max-w-736 px-16 relative">
      <ProfileEdit :account="account" />
      <OnboardingTasks :account="account" />
      <ResourceCards class="mt-32" />
      <hr v-if="sitterProfileCompletenessEnabled" class="border-grey-divider-subdued my-32" />
      <ProfileProgress v-if="sitterProfileCompletenessEnabled" />
      <WorkLikeAPro
        :sitterId="account.id"
        :sitterName="`${account.firstName} ${account.lastName ? account.lastName[0] : ''}.`"
        :initialShowReviewModal="showReviewModal"
      />
    </div>
    <InfoJustForYou />
  </div>
</template>

<script lang="ts">
import { sendBrazeEvent } from '../../../lib/braze';
import OnboardingTasks from './onboarding_tasks/OnboardingTasks.vue';
import ProfileEdit from './profile/ProfileEdit.vue';
import ResourceCards from './ResourceCards.vue';
import WorkLikeAPro from './WorkLikeAPro.vue';
import InfoJustForYou from './InfoJustForYou.vue';
import ProfileProgress from './profile_progress/ProfileProgress.vue';
import { SITTER_DASHBOARD_LOADED, SHOW_SITTER_SMS_MODAL } from '@/lib/constants/braze_custom_events';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import { readDomainCookie, setPhotoRejectCookie, deleteDomainCookie } from '@/lib/domain_cookie';
import { readMetaBoolean, shouldShowSMSModal } from '../../../lib/page';

export default {
  name: 'SitterDashboard',
  components: { OnboardingTasks, ProfileEdit, ResourceCards, InfoJustForYou, WorkLikeAPro, AlertBanner, ProfileProgress },

  data() {
    return {
      hasPhotoRejected: false,
      timeout: null as NodeJS.Timeout | null,
    };
  },

  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    sitterProfileCompletenessEnabled() {
      return readMetaBoolean('sitter-profile-completeness-meter');
    },
    isPhotoUploadAlert(): boolean {
      const isPhotoAlert = readDomainCookie('profilePhotoAlert_success') || false;
      deleteDomainCookie('profilePhotoAlert_success');

      // If photo upload was successful, clear the rejection cookie to prevent showing both banners
      if (isPhotoAlert === 'true' && this.account) {
        deleteDomainCookie(`profilePhotoAlert-${this.account.id}`);
        this.hasPhotoRejected = false;
      }

      return isPhotoAlert === 'true';
    },
    hasExternalReviewSitterError(): boolean {
      const urlParams = new URLSearchParams(window.location.search);
      return !!urlParams.get('ser');
    },
    showReviewModal(): boolean {
      const urlParams = new URLSearchParams(window.location.search);
      const reviewLinkDisplayed = urlParams.get('reviewLinkDisplayed');
      urlParams.delete('reviewLinkDisplayed');

      // Remove the query param from the URL without reloading the page
      const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
      window.history.replaceState(null, '', newUrl);

      return !!reviewLinkDisplayed;
    },
  },

  mounted() {
    sendBrazeEvent(SITTER_DASHBOARD_LOADED);

    if (this.shouldShowSMSModal()) {
      sendBrazeEvent(SHOW_SITTER_SMS_MODAL);
    }

    if (this.account) {
      this.hasPhotoRejected = setPhotoRejectCookie(
        this.account.profilePhotoStatus,
        `profilePhotoAlert-${this.account.id}`,
        'True',
        10
      );
    }

    this.timeout = setTimeout(this.clearAlertBanner, 15000);
  },

  methods: {
    /**
     * Determines if the SMS modal should be shown based on account creation date.
     * Delegates to the shared utility function in lib/page.ts.
     */
    shouldShowSMSModal(): boolean {
      return shouldShowSMSModal(this.account?.createdAt);
    },
    clearAlertBanner() {
      this.hasPhotoRejected = false;
    },
  },
};
</script>

<style lang="scss">
#external-reviews-sitter-error {
  @media only screen and (min-width: 768px) {
    margin-top: -30px;
  }
  @media only screen and (min-width: 1014px) {
    margin-top: 0px;
  }
}
</style>
